/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

"use client";

import { useSearchParams, notFound } from "next/navigation";
import { Suspense, useState } from "react";

function ErrorTestContent(): React.JSX.Element {
	const searchParams = useSearchParams();
	const errorType = searchParams.get("type");
	const [shouldThrowError, setShouldThrowError] = useState(false);

	const triggerError = () => {
		if (errorType === "500") {
			// Trigger a 500 error by throwing an uncaught exception
			throw new Error("Test 500 error: This is a simulated server error for testing purposes");
		} else if (errorType === "404") {
			// Trigger a 404 error
			notFound();
		}
	};

	// Auto-trigger error based on query parameter
	if (errorType === "500" || errorType === "404") {
		triggerError();
	}

	// This will trigger during render if shouldThrowError is true
	if (shouldThrowError) {
		throw new Error("Test 500 error: React error boundary trigger from error test page");
	}

	const triggerEventHandlerError = () => {
		// This error won't be caught by React error boundaries
		// But will be caught by window.error event listener
		setTimeout(() => {
			throw new Error("Test 500 error: Event handler error from error test page");
		}, 0);
	};

	const triggerPromiseRejection = () => {
		// This will be caught by window.unhandledrejection event listener
		void Promise.reject(new Error("Test 500 error: Unhandled promise rejection from error test page"));
	};

	return (
		<div style={{ padding: "2rem", fontFamily: "system-ui" }}>
			<h1>Error Test Page</h1>
			<p>Use this page to test different types of error handling in the application.</p>

			<div style={{ marginTop: "2rem" }}>
				<h2>Manual Error Triggers</h2>
				<div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
					<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
						<button
							onClick={() => setShouldThrowError(true)}
							style={{
								padding: "0.5rem 1rem",
								backgroundColor: "#dc2626",
								color: "white",
								border: "none",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Trigger React Error Boundary
						</button>

						<button
							onClick={triggerEventHandlerError}
							style={{
								padding: "0.5rem 1rem",
								backgroundColor: "#ea580c",
								color: "white",
								border: "none",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Trigger Window Error
						</button>

						<button
							onClick={triggerPromiseRejection}
							style={{
								padding: "0.5rem 1rem",
								backgroundColor: "#ca8a04",
								color: "white",
								border: "none",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Trigger Promise Rejection
						</button>
					</div>
				</div>
			</div>

			<div style={{ marginTop: "2rem" }}>
				<h2>Error Types Explained</h2>
				<div style={{ marginTop: "1rem", lineHeight: "1.6" }}>
					<div style={{ marginBottom: "1rem" }}>
						<strong style={{ color: "#dc2626" }}>React Error Boundary:</strong> Caught by React error boundaries,
						triggers error.tsx page
					</div>
					<div style={{ marginBottom: "1rem" }}>
						<strong style={{ color: "#ea580c" }}>Window Error:</strong> Caught by window.error listener, triggers error
						reporting
					</div>
					<div style={{ marginBottom: "1rem" }}>
						<strong style={{ color: "#ca8a04" }}>Promise Rejection:</strong> Caught by window.unhandledrejection
						listener
					</div>
				</div>
			</div>

			<div style={{ marginTop: "2rem" }}>
				<h2>URL-based Error Triggers</h2>
				<p>You can also trigger errors using URL parameters:</p>
				<ul>
					<li>
						<a href="/error-test?type=500" style={{ color: "#2563eb" }}>
							/error-test?type=500
						</a>
						{" - Triggers a React error boundary"}
					</li>
					<li>
						<a href="/error-test?type=404" style={{ color: "#2563eb" }}>
							/error-test?type=404
						</a>
						{" - Triggers a 404 error"}
					</li>
				</ul>
			</div>
		</div>
	);
}

export function ErrorTestPage(): React.JSX.Element {
	return (
		<Suspense fallback={<div>Loading...</div>}>
			<ErrorTestContent />
		</Suspense>
	);
}
